import { useEffect, useState } from "react";
import axios from "axios";
import { API, getAuthHeader } from "@/App";
import { toast } from "sonner";
import { Download, Loader2, FlaskConical, TrendingUp, TrendingDown, Minus, Sparkles, AlertTriangle, Settings2 } from "lucide-react";

const istNow = () => new Date(Date.now() + 330 * 60000);
const iso = (d) => d.toISOString().split("T")[0];
const daysAgo = (n) => { const d = istNow(); d.setUTCDate(d.getUTCDate() - n); return iso(d); };

const Trend = ({ r }) => {
  if (r.is_new) return <span className="inline-flex items-center gap-1 rounded-full bg-sky-100 text-sky-700 px-2 py-0.5 text-[11px] font-semibold"><Sparkles className="w-3 h-3" />NEW</span>;
  const up = r.change_pct > 0, flat = r.change_pct === 0;
  const Icon = flat ? Minus : up ? TrendingUp : TrendingDown;
  const tone = flat ? "text-slate-500" : up ? "text-emerald-600" : "text-rose-600";
  return <span className={`inline-flex items-center gap-1 text-xs font-semibold ${tone}`}><Icon className="w-3.5 h-3.5" />{r.change_pct > 999 ? ">+999" : `${up ? "+" : ""}${r.change_pct}`}%</span>;
};

const DemandTable = ({ rows, testid }) => (
  <table className="w-full border-collapse" data-testid={testid}>
    <thead>
      <tr>
        {["#", "Product (2nd Name)", "Total Qty", "Prev Period", "Trend", "Order Rows", "Clients"].map((h, i) => (
          <th key={h} className={`bg-[#2F5496] text-white text-sm font-semibold py-2 px-3 border border-slate-300 ${i === 1 ? "text-left" : "text-center"}`}>{h}</th>
        ))}
      </tr>
    </thead>
    <tbody>
      {rows.length === 0 && <tr><td colSpan={7} className="p-6 text-center text-slate-500">No products in this period.</td></tr>}
      {rows.map(r => (
        <tr key={r.product} className={r.restock ? "bg-orange-50/70 hover:bg-orange-50" : "hover:bg-slate-50"} data-testid={`${testid}-row-${r.rank}`}>
          <td className="py-1.5 px-3 text-sm text-center text-slate-500 border border-slate-200">{r.rank}</td>
          <td className="py-1.5 px-3 text-sm font-semibold text-slate-800 border border-slate-200">{r.product}
            {r.restock && <span className="ml-2 inline-flex items-center gap-1 rounded-full bg-orange-500 text-white px-2 py-0.5 text-[10px] font-bold" data-testid={`${testid}-restock-${r.rank}`}><AlertTriangle className="w-3 h-3" />RESTOCK</span>}</td>
          <td className="py-1.5 px-3 text-sm text-center font-bold text-slate-900 border border-slate-200">{r.qty.toLocaleString("en-IN")}</td>
          <td className="py-1.5 px-3 text-sm text-center text-slate-500 border border-slate-200">{r.prev_qty ? r.prev_qty.toLocaleString("en-IN") : "—"}</td>
          <td className="py-1.5 px-3 text-center border border-slate-200" data-testid={`${testid}-trend-${r.rank}`}><Trend r={r} /></td>
          <td className="py-1.5 px-3 text-sm text-center text-slate-700 border border-slate-200">{r.orders}</td>
          <td className="py-1.5 px-3 text-sm text-center text-slate-700 border border-slate-200">{r.clients}</td>
        </tr>
      ))}
    </tbody>
  </table>
);

// Top-50 products by quantity (Order Register, 2nd name). Raw/API list is admin-only (server-enforced).
const RestockSettings = ({ s, onSaved }) => {
  const [open, setOpen] = useState(false);
  const [v, setV] = useState(s);
  useEffect(() => setV(s), [s]);
  const save = async () => {
    try {
      const r = await axios.put(`${API}/reports/restock-settings`, v, { headers: getAuthHeader() });
      toast.success("Restock alert rule saved"); setOpen(false); onSaved(r.data);
    } catch (e) { toast.error(e.response?.data?.detail || "Could not save"); }
  };
  return (
    <div className="relative">
      <button onClick={() => setOpen(!open)} data-testid="restock-settings-btn" className="inline-flex items-center gap-1 px-2 py-1 text-xs rounded-lg border border-orange-300 bg-white text-orange-800 hover:bg-orange-50">
        <Settings2 className="w-3.5 h-3.5" /> Alert rule
      </button>
      {open && (
        <div className="absolute right-0 top-full mt-1 z-30 w-64 bg-white border border-slate-200 rounded-lg shadow-xl p-3 space-y-2 text-xs" data-testid="restock-settings-panel">
          <label className="flex items-center justify-between">Up by at least (%)<input type="number" value={v?.min_change_pct ?? ""} onChange={e => setV({ ...v, min_change_pct: e.target.value })} className="w-20 px-2 py-1 border rounded text-right" data-testid="restock-pct" /></label>
          <label className="flex items-center justify-between">Min qty this period<input type="number" value={v?.min_qty ?? ""} onChange={e => setV({ ...v, min_qty: e.target.value })} className="w-20 px-2 py-1 border rounded text-right" data-testid="restock-qty" /></label>
          <button onClick={save} className="w-full px-3 py-1.5 rounded-lg bg-orange-600 hover:bg-orange-700 text-white font-semibold" data-testid="restock-save">Save</button>
        </div>
      )}
    </div>
  );
};

const RestockAlerts = ({ data, isAdmin, onSaved }) => {
  const rows = [...(data.products || []), ...(data.raw_products || [])].filter(r => r.restock);
  const s = data.restock_settings;
  return (
    <div className={`rounded-xl border p-3 ${rows.length ? "border-orange-300 bg-orange-50" : "border-slate-200 bg-slate-50"}`} data-testid="restock-alerts">
      <div className="flex items-center gap-2">
        <AlertTriangle className={`w-4 h-4 ${rows.length ? "text-orange-600" : "text-slate-400"}`} />
        <span className="text-sm font-semibold text-slate-800" data-testid="restock-count">{rows.length} restock alert{rows.length === 1 ? "" : "s"}</span>
        {s && <span className="text-[11px] text-slate-500">demand up ≥{s.min_change_pct}% vs previous period and ≥{s.min_qty} units</span>}
        {isAdmin && <div className="ml-auto"><RestockSettings s={s} onSaved={onSaved} /></div>}
      </div>
      {rows.length > 0 && (
        <div className="mt-2 flex flex-wrap gap-2">
          {rows.map(r => (
            <span key={r.product} className="rounded-lg bg-white border border-orange-200 px-2 py-1 text-xs" data-testid={`restock-chip-${r.rank}`}>
              <b>{r.product}</b> · {r.qty.toLocaleString("en-IN")} <span className="text-slate-400">(was {r.prev_qty.toLocaleString("en-IN")})</span> <span className="text-emerald-700 font-semibold">{r.change_pct > 999 ? ">+999" : `+${r.change_pct}`}%</span>
            </span>
          ))}
        </div>
      )}
    </div>
  );
};

export const ProductDemandReport = ({ isAdmin }) => {
  const [tick, setTick] = useState(0);
  const [from, setFrom] = useState(daysAgo(6));
  const [to, setTo] = useState(iso(istNow()));
  const [data, setData] = useState(null);
  useEffect(() => {
    setData(null);
    axios.get(`${API}/reports/product-demand`, { headers: getAuthHeader(), params: { date_from: from, date_to: to } })
      .then(r => setData(r.data)).catch(() => { setData({ products: [] }); toast.error("Could not load product demand"); });
  }, [from, to, tick]);
  const download = async () => {
    try {
      const r = await axios.get(`${API}/reports/product-demand/excel`, { headers: getAuthHeader(), params: { date_from: from, date_to: to }, responseType: "blob" });
      const a = document.createElement("a");
      a.href = URL.createObjectURL(new Blob([r.data]));
      a.download = `Product_Demand_${from}_${to}.xlsx`;
      a.click();
    } catch { toast.error("Excel download failed"); }
  };
  return (
    <div className="p-4 pt-2 space-y-4" data-testid="product-demand-report">
      <div className="flex flex-wrap items-center gap-2">
        <input type="date" value={from} onChange={e => setFrom(e.target.value)} data-testid="demand-from" className="px-2 py-1.5 border border-slate-300 rounded-lg text-sm" />
        <span className="text-slate-400 text-sm">to</span>
        <input type="date" value={to} onChange={e => setTo(e.target.value)} data-testid="demand-to" className="px-2 py-1.5 border border-slate-300 rounded-lg text-sm" />
        <button onClick={download} data-testid="demand-excel-btn" className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white">
          <Download className="w-3.5 h-3.5" /> Excel
        </button>
        <span className="text-[11px] text-slate-500" data-testid="demand-prev-range">Top 50 by quantity · cancelled excluded · order date{data?.prev_from ? ` · trend vs ${data.prev_from} → ${data.prev_to}` : ""}</span>
      </div>
      {!data ? <div className="p-8 text-center"><Loader2 className="w-5 h-5 animate-spin inline" /></div> : (
        <>
          <RestockAlerts data={data} isAdmin={isAdmin} onSaved={() => setTick(t => t + 1)} />
          <DemandTable rows={data.products || []} testid="demand-table" />
          {data.raw_products && (
            <div data-testid="demand-raw-section">
              <h4 className="flex items-center gap-1.5 text-sm font-semibold text-amber-800 mb-2">
                <FlaskConical className="w-4 h-4" /> Raw / API products <span className="text-[11px] font-normal text-slate-500">(admin only)</span>
              </h4>
              <DemandTable rows={data.raw_products} testid="demand-raw-table" />
            </div>
          )}
        </>
      )}
    </div>
  );
};
